import { useEffect, type RefObject } from 'react';

/**
 * Lets the consent banner float ABOVE whatever bottom-pinned bar is on screen,
 * without any bar having to move.
 *
 * ## Why this exists, given its predecessor was deleted
 *
 * There have been three arrangements, and the difference between the second
 * and third is the whole point:
 *
 *   1. The banner sat at `bottom: 0` over everything. On `/quiz` it covered the
 *      sticky Continue outright — present in the DOM, invisible and
 *      unclickable — so a visitor who neither accepted nor declined could not
 *      finish the quiz. The primary conversion funnel, live in production.
 *
 *   2. `--thea-consent-inset`: the banner published its height and every
 *      bottom-pinned bar added it to their own `bottom`. That fixed the
 *      overlap by MOVING THE BARS, which on mobile shoved the tab bar into the
 *      middle of the screen. It read as broken layout rather than as a notice
 *      (Kate, 2026-09-04), and it was removed.
 *
 *   3. This: the direction is reversed. Bars publish their height; the BANNER
 *      lifts itself above them. Nothing the user was already looking at moves —
 *      which was the actual complaint — and the Continue button stays
 *      reachable, which was the actual incident.
 *
 * So the rule is not "never couple the banner to the bars". It is **the banner
 * yields to the page, never the other way round.**
 *
 * ## Why a registry and not one variable per bar
 *
 * More than one bar can be mounted at once (a tab bar plus a page's own action
 * bar). A plain custom property would be last-writer-wins, and the loser's bar
 * would be covered. The registry publishes the MAX height of everything
 * currently registered, so the banner clears the tallest — the only value that
 * is safe for all of them.
 *
 * Only bars that BLOCK PROGRESS need to register. Toasts, spacers and panels
 * are fine to be overlapped by a notice the user can dismiss; a Continue button
 * is not.
 */

/** The custom property the banner reads. Published on `<html>`. */
export const BOTTOM_BAR_INSET_VAR = '--thea-bottom-bar-inset';

/** Every currently-mounted blocking bar. */
const bars = new Set<HTMLElement>();

let observer: ResizeObserver | undefined;

function publish(): void {
  if (typeof document === 'undefined') return;
  let tallest = 0;
  bars.forEach((el) => {
    // offsetHeight covers the bar's own safe-area padding, so the banner does
    // not have to add it back a second time.
    const height = el.offsetHeight;
    if (height > tallest) tallest = height;
  });
  document.documentElement.style.setProperty(BOTTOM_BAR_INSET_VAR, `${tallest}px`);
}

/**
 * Register a bottom-pinned bar the consent banner must not cover.
 *
 * Returns an unregister function. Safe to call with the same element twice.
 */
export function registerBottomBar(el: HTMLElement): () => void {
  bars.add(el);

  if (typeof ResizeObserver !== 'undefined') {
    if (!observer) observer = new ResizeObserver(publish);
    observer.observe(el);
  }
  publish();

  return () => {
    bars.delete(el);
    observer?.unobserve(el);
    // Publish AFTER removal so the banner drops back down the moment the last
    // bar unmounts — otherwise it floats above empty space for the rest of the
    // session, which looks like a rendering bug.
    publish();
  };
}

/**
 * Hook form. Give it a ref to the fixed bar itself, not a wrapper — the
 * measured height is what the banner clears.
 */
export function useBottomBarInset(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    return registerBottomBar(el);
  }, [ref]);
}

/** Test-only: drop all registrations and reset the property. */
export function __resetBottomBarsForTests(): void {
  bars.clear();
  observer?.disconnect();
  observer = undefined;
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty(BOTTOM_BAR_INSET_VAR, '0px');
  }
}
